import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { ArrowLeft, FileText, AlertCircle, Download, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { getInspectionPersonnelDocuments } from '../../../../../redux/Project/materialsData/action';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';

interface ConformityReportsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateClick?: () => void;
  projectId: number | string;
  projectName: string;
  /** Refetch list after approval (same pattern as equipment reports). */
  refresh?: () => void;
}

const CONFORMITY_KEYS = new Set([
  REPORT_TYPES.CERTIFICATE_OF_CONFORMITY_REPORT,
  'CERTIFICATE_OF_CONFORMITY_REPORT',
  'CONFORMITY_REPORT',
  'conformity_report',
  'ConformityReport'
]);

function isConformityReportType(reportType: string): boolean {
  if (CONFORMITY_KEYS.has(reportType)) return true;
  const u = reportType.toUpperCase();
  return u.includes('CONFORMITY');
}

export const ConformityReportsViewModal: React.FC<ConformityReportsViewModalProps> = ({
  isOpen,
  onClose,
  onGenerateClick,
  projectId,
  projectName,
  refresh
}) => {
  const dispatch = useDispatch();
  const {
    inspectionPersonnelDocumentsLoading,
    inspectionPersonnelDocuments,
    inspectionPersonnelDocumentsError
  } = useSelector((state: any) => state.materialsData || {});

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  useEffect(() => {
    if (isOpen && projectId) {
      dispatch(getInspectionPersonnelDocuments(projectId));
    } else if (!isOpen) {
      setExpandedGroups(new Set());
      setViewerFile(null);
    }
  }, [isOpen, projectId, dispatch]);

  const formatReportType = (reportType: string): string => {
    return reportType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const processedVersions = useMemo(() => {
    if (!inspectionPersonnelDocuments || typeof inspectionPersonnelDocuments !== 'object') {
      return {};
    }
    const result: Record<string, { latest: any | null; older: any[]; total: number }> = {};

    Object.keys(inspectionPersonnelDocuments).forEach((reportType) => {
      if (!isConformityReportType(reportType)) return;
      const docs = inspectionPersonnelDocuments[reportType] || [];
      if (docs.length === 0) return;

      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });

      result[reportType] = {
        latest: sorted[0] || null,
        older: sorted.slice(1),
        total: sorted.length
      };
    });

    return result;
  }, [inspectionPersonnelDocuments]);

  const totalDocumentsCount = useMemo(() => {
    return Object.values(processedVersions).reduce((sum, group) => sum + (group?.total || 0), 0);
  }, [processedVersions]);

  const toggleGroupExpansion = (reportType: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(reportType)) next.delete(reportType);
      else next.add(reportType);
      return next;
    });
  };

  const handleDownloadReport = (doc: any) => {
    if (!doc || !(doc.fileUrl || doc.file_url)) return;
    setViewerFile({
      fileUrl: doc.fileUrl || doc.file_url,
      fileName: doc.fileName || doc.file_name || 'Conformity Report'
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const closeViewer = () => setViewerFile(null);

  if (viewerFile) {
    return (
      <PdfViewer fileUrl={viewerFile.fileUrl} fileName={viewerFile.fileName} onClose={closeViewer} />
    );
  }

  const isLoading = inspectionPersonnelDocumentsLoading;
  const error = inspectionPersonnelDocumentsError;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden">
        <div className="flex flex-col relative z-20 h-screen overflow-hidden">
          <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button onClick={onClose} variant="outline" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div>
                <DialogTitle className="text-lg font-bold text-gray-900">Conformity Reports</DialogTitle>
                {projectName ? <p className="text-sm text-gray-600 mt-1">Project: {projectName}</p> : null}
                {!isLoading && totalDocumentsCount > 0 ? (
                  <p className="text-xs text-gray-500 mt-0.5">{totalDocumentsCount} document(s)</p>
                ) : null}
              </div>
            </div>
            {onGenerateClick ? (
              <Button
                type="button"
                onClick={onGenerateClick}
                className="flex-shrink-0 bg-hero-red hover:bg-hero-red-60 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Conformity Report
              </Button>
            ) : null}
          </DialogHeader>

          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 relative">
            <div className="space-y-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-500 mr-2" />
                  <span className="text-sm text-gray-500">Loading conformity reports…</span>
                </div>
              ) : error ? (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              ) : Object.keys(processedVersions).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-gray-600 gap-3 border border-dashed border-gray-200 rounded-lg">
                  <FileText className="h-12 w-12 text-gray-300" />
                  <p className="text-sm max-w-md">
                    No conformity reports found for this project yet. Generate one from &quot;Generate Conformity
                    Report&quot; once the backend stores them under the conformity report type in this API response.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(processedVersions).map(([reportType, { latest, older, total }]) => {
                    if (!latest) return null;
                    const isExpanded = expandedGroups.has(reportType);
                    const hasOlderVersions = older.length > 0;

                    return (
                      <div key={reportType} className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide pb-2 border-b">
                          {formatReportType(reportType)} ({total})
                        </h4>

                        <div className="flex items-center justify-between p-4 rounded-lg transition-colors border bg-blue-50 hover:bg-blue-100 border-blue-200">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {latest.fileName || 'Conformity Report'}
                                </p>
                                {latest.docRef ? (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                                    v{latest.docRef}
                                  </span>
                                ) : null}
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                                  Latest
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                <span>{formatDate(latest.uploadDate || '')}</span>
                                <span>{latest.uploadedBy || ''}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                            {hasOlderVersions ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleGroupExpansion(reportType)}
                                className="flex items-center gap-1"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp className="h-4 w-4" />
                                    Hide versions
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-4 w-4" />
                                    Versions ({older.length})
                                  </>
                                )}
                              </Button>
                            ) : null}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadReport(latest)}
                              disabled={!latest.fileUrl}
                              className="flex items-center gap-2"
                            >
                              <Download className="h-4 w-4" />
                              View / Download
                            </Button>
                            <ApproveButton
                              docId={latest.docId}
                              isApproved={latest.approvalStatus === 'approved'}
                              apiType="registry"
                              onStatusChange={() => refresh?.()}
                            />
                          </div>
                        </div>

                        {hasOlderVersions && isExpanded ? (
                          <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-4">
                            {older.map((doc: any, index: number) => (
                              <div
                                key={doc.docId || index}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-gray-700 truncate">
                                        {doc.fileName || 'Conformity Report'}
                                      </p>
                                      {doc.docRef ? (
                                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                                          v{doc.docRef}
                                        </span>
                                      ) : null}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">{formatDate(doc.uploadDate || '')}</div>
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadReport(doc)}
                                  disabled={!doc.fileUrl}
                                  className="flex items-center gap-2 flex-shrink-0"
                                >
                                  <Download className="h-4 w-4" />
                                  View / Download
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
